import {action, computed, makeObservable, observable, runInAction} from 'mobx';
import {browserHistory} from 'routing/browserHistory';
import {showCommunicationError, showSuccess} from 'helpers/alerts';
import {t} from 'i18next';
import {
  CarDto,
  CarListDto,
  CustomerDto,
  ReservationCreateUpdateDto,
  ReservationDto,
  ServiceActionDto,
} from '../../../../../PRMGApiClient';
import {Api} from '../../../../../PRMGApiClient/ApiClient';
import {ReservationViewModel} from 'views/reservations/ReservationEditView';
import {getReservationsActiveDates} from '../../helpers/utils';
import {AppRoutePaths} from 'routing/Routes';

export class ReservationState {
  @observable
  item: ReservationCreateUpdateDto = {
    id: undefined,
    carId: undefined,
    customerId: undefined,
    description: undefined,
    begins: undefined,
    ends: undefined,
  };

  @observable
  reservation: ReservationDto = {};

  @observable
  allCarReservations?: Array<ReservationDto> | null;

  @observable
  availableCars: Array<CarListDto> = [];

  @observable
  customers: Array<CustomerDto> = [];

  @observable
  isLoading = false;

  constructor() {
    makeObservable(this);
    this.getCustomers();
  }

  @action
  saveCar(car: CarDto): void {
    this.reservation.car = car ?? this.reservation?.car;
  }

  @computed
  get reservationViewModel(): ReservationViewModel {
    return {
      customerId: this.reservation?.customer?.customerId,
      carId: this.reservation?.car?.carId,
      carMakeId: this.reservation?.car?.carMakeId,
      carModelId: this.reservation?.car?.carModelId,
      ...this.reservation,
    };
  }

  @computed
  get carReservationsDatesWithoutCurrent(): Date[] {
    return getReservationsActiveDates(this.allCarReservations);
  }

  loadReservation(reservationId: number) {
    Api.Reservation.apiReservationGetIdGet({
      id: reservationId,
    })
      .then((res) => {
        res.content?.car?.carId && this.loadAllCarReservations(res.content?.car?.carId);
        runInAction(() => {
          this.reservation = res.content ?? this.reservation;
        });
      })
      .catch(showCommunicationError);
  }

  loadAllCarReservations(carId: number) {
    Api.Reservation.apiReservationFindByGet({
      filtersCarId: carId,
    })
      .then((res) => {
        this.allCarReservations = res.content?.items;
      })
      .catch(showCommunicationError);
  }

  getAvailableCars(startDate: Date, endDate: Date, makeId?: number, modelId?: number) {
    Api.Car.getCarsAvailableForReservations({
      carMakeId: makeId,
      carModelId: modelId,
      from: startDate,
      until: endDate,
    })
      .then((res) => {
        runInAction(() => {
          this.availableCars = res.content ?? [];
        });
      })
      .catch(showCommunicationError);
  }

  getCustomers() {
    Api.Customer.apiCustomerGetAllGet({})
      .then((res) => {
        runInAction(() => {
          this.customers = res.content?.items ?? [];
        });
      })
      .catch(showCommunicationError);
  }

  @action
  saveItem(newItem: ServiceActionDto) {
    this.item = newItem ?? this.item;
  }

  @action
  save(payload: ServiceActionDto): void {
    this.item = {...payload};
    if (payload.carId) {
      payload.id ? this.updateReservation() : this.addReservation();
    }
  }

  @action
  addReservation(): void {
    this.isLoading = true;
    Api.Reservation.apiReservationAddPost({
      reservationCreateUpdateDto: this.item,
    })
      .then(() => {
        showSuccess(t('Reservation added'));
        browserHistory.back();
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  @action
  updateReservation(): void {
    this.isLoading = true;
    Api.Reservation.apiReservationUpdatePut({
      reservationCreateUpdateDto: this.item,
    })
      .then(() => {
        showSuccess(t('Changes saved'));
        browserHistory.back();
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  @action
  deleteReservation(): void {
    this.isLoading = true;
    if (this.reservation?.id) {
      Api.Reservation.apiReservationDeleteIdDelete({
        id: this.reservation?.id,
      })
        .then(() => {
          showSuccess(t('Changes saved'));
          browserHistory.push(AppRoutePaths.reservations.url);
        })
        .catch(showCommunicationError)
        .finally(() => (this.isLoading = false));
    }
  }
}
